import { useEffect, useState } from "react";
import axios from "axios";
import { takeBudgetRequest } from "@/estimating/lib/navigation";
import {
  MemoryRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useNavigationType,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
} from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import BudgetBuilder from "./pages/BudgetBuilder";
import BudgetSummary from "./pages/BudgetSummary";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminScopes from "./pages/admin/AdminScopes";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminManufacturers from "./pages/admin/AdminManufacturers";
import SharedSubTabs from "@/components/SubTabs";

// The ported pages use the GLOBAL axios instance and rely on axios.defaults for
// auth. Set the main app's bearer token synchronously (at import + render) so the
// first data fetch on mount is authenticated.
function applyAuthHeader() {
  const t = typeof localStorage !== "undefined" ? localStorage.getItem("auth_token") : null;
  if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
}
applyAuthHeader();

const EMPTY_ROUTE_CTX = { outlet: null, matches: [], isDataRoute: false };

const SUBTABS = [
  { key: "admin-dashboard", label: "Dashboard", to: "/admin/dashboard", match: (p) => p.startsWith("/admin/dashboard") },
  { key: "budgets", label: "Budgets", to: "/dashboard", match: (p) => p === "/dashboard" || p.startsWith("/budgets") },
  { key: "pricing", label: "Pricing", to: "/admin/pricing", match: (p) => p.startsWith("/admin/pricing") },
  { key: "scopes", label: "Scopes", to: "/admin/scopes", match: (p) => p.startsWith("/admin/scopes") },
  { key: "manufacturers", label: "Manufacturers", to: "/admin/manufacturers", match: (p) => p.startsWith("/admin/manufacturers") },
];

function SubTabs() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <SharedSubTabs
      tabs={SUBTABS.map((t) => ({ key: t.key, label: t.label, active: t.match(pathname),
                                  testId: `budget-subtab-${t.key}` }))}
      onChange={(key) => navigate(SUBTABS.find((t) => t.key === key).to)}
      testId="budget-subtabs"
      divider={false}
    />
  );
}

// Picks up an openBudget() request on mount (tab switch) and while mounted.
function BudgetOpener() {
  const navigate = useNavigate();
  useEffect(() => {
    const go = () => { const id = takeBudgetRequest(); if (id) navigate(`/budgets/${id}`); };
    go();
    window.addEventListener("abs:open-budget", go);
    return () => window.removeEventListener("abs:open-budget", go);
  }, [navigate]);
  return null;
}

const HOME = "/admin/dashboard";

// Mirrors the module's in-memory route into the app URL (/budgets?b=…) and back, so every
// Budgets page is a real browser history entry and Back / Forward restore it.
function OuterSync({ outerPath, pushOuter }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const navType = useNavigationType();
  const inner = loc.pathname + loc.search;
  useEffect(() => {
    if (outerPath !== inner) navigate(outerPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outerPath]);
  useEffect(() => {
    if (inner !== outerPath) pushOuter(inner, navType === "REPLACE");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inner]);
  return null;
}

export default function BudgetApp() {
  applyAuthHeader();
  const outerLoc = useLocation();
  const outerNav = useNavigate();
  const outerPath = new URLSearchParams(outerLoc.search).get("b") || HOME;
  const pushOuter = (p, replace) => outerNav(p === HOME ? "/budgets" : `/budgets?b=${encodeURIComponent(p)}`, { replace });
  // Keep MemoryRouter mounted for the app lifetime so internal nav state persists.
  const [key] = useState(0);
  const [initial] = useState(outerPath);
  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="budget-app">
      {/* react-router v7 throws "You cannot render a <Router> inside another
          <Router>" when nesting. Reset the outer router contexts so the
          module's MemoryRouter can mount inside the app's BrowserRouter. */}
      <UNSAFE_LocationContext.Provider value={null}>
        <UNSAFE_RouteContext.Provider value={EMPTY_ROUTE_CTX}>
          <MemoryRouter key={key} initialEntries={[initial]}>
            <OuterSync outerPath={outerPath} pushOuter={pushOuter} />
            <SubTabs />
            <BudgetOpener />
            <div className="flex-1 overflow-auto bg-background">
              <Routes>
                <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/budgets/new" element={<BudgetBuilder />} />
                <Route path="/budgets/:id" element={<BudgetBuilder />} />
                <Route path="/budgets/:id/summary" element={<BudgetSummary />} />
                <Route path="/admin/pricing" element={<AdminPricing />} />
                <Route path="/admin/scopes" element={<AdminScopes />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/manufacturers" element={<AdminManufacturers />} />
                <Route path="/admin/users" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/activity" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </div>
          </MemoryRouter>
        </UNSAFE_RouteContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </div>
  );
}
